import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { ROLE_DEFINITIONS } from '../../../../services/roleService';

/** A dialog that only needs a note (Return to L1, Not Approved, Cancel, Return resolution). */
export function NoteDialog({ open, title, body, label = 'Note', confirmLabel = 'Confirm', confirmColor = 'primary', required = true, onClose, onConfirm, busy = false }) {
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {body && <DialogContentText sx={{ mb: 2 }}>{body}</DialogContentText>}
        <TextField autoFocus fullWidth multiline minRows={3} label={label} value={note} onChange={(e) => setNote(e.target.value)} required={required} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color={confirmColor} disabled={busy || (required && !note.trim())} onClick={() => onConfirm(note.trim())}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Mark as Resolved (P2-12, D06 §5.3): a note is required; corrective actions stay a single note for
 * Beta-1 but the dialog is built so several can be listed later (P2-30, C0820 "one, two, three,
 * four corrective actions… once all done, closed").
 */
export function ResolveDialog({ open, condition, direct, onClose, onConfirm, busy = false }) {
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mark as Resolved</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {direct
            ? 'This condition is still Pending Review — resolving it now records it as found and fixed by you.'
            : 'Describe what was done. Your resolution goes to the L1 reviewer and then the L2 reviewer for approval before the condition is closed.'}
        </DialogContentText>
        <TextField autoFocus fullWidth multiline minRows={4} required label="What was done to resolve this condition?" value={note} onChange={(e) => setNote(e.target.value)} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {condition?.conditionId ? `${condition.conditionId} · ` : ''}
          {condition?.title}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color="success" disabled={busy || !note.trim()} onClick={() => onConfirm(note.trim())}>
          Submit Resolution
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Owner picker (P2-04 / P2-14): department (grouped by site) → optional NWT → optional person in that department. */
export function OwnerFields({ org, members = [], value, onChange, allowPerson = true, size = 'small' }) {
  const { ownerDeptId = '', ownerNwtId = '', ownerUid = '' } = value || {};
  const deptOptions = useMemo(
    () =>
      (org?.sites || []).flatMap((site) =>
        (org?.departments || []).filter((d) => d.siteId === site.id).map((d) => ({ id: d.id, label: `${d.name} — ${site.name}` }))
      ),
    [org]
  );
  const nwtOptions = (org?.teams || []).filter((t) => t.departmentId === ownerDeptId);
  const people = useMemo(() => {
    if (!ownerDeptId) return members;
    const deptTeams = new Set((org?.teams || []).filter((t) => t.departmentId === ownerDeptId).map((t) => t.id));
    const inDept = members.filter((m) => m.scopeId === ownerDeptId || deptTeams.has(m.scopeId));
    const rest = members.filter((m) => !inDept.includes(m));
    return [...inDept.map((m) => ({ ...m, inDept: true })), ...rest];
  }, [members, org, ownerDeptId]);
  const set = (patch) => onChange({ ownerDeptId, ownerNwtId, ownerUid, ...patch });
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
      <TextField select size={size} label="Owner department" value={ownerDeptId} onChange={(e) => set({ ownerDeptId: e.target.value, ownerNwtId: '', ownerUid: '' })} sx={{ minWidth: 220, flex: 1 }}>
        <MenuItem value="">
          <em>Not set</em>
        </MenuItem>
        {deptOptions.map((d) => (
          <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
        ))}
      </TextField>
      <TextField select size={size} label="Owner NWT (optional)" value={ownerNwtId} onChange={(e) => set({ ownerNwtId: e.target.value })} disabled={!ownerDeptId} sx={{ minWidth: 200, flex: 1 }}>
        <MenuItem value="">
          <em>Whole department</em>
        </MenuItem>
        {nwtOptions.map((t) => (
          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
        ))}
      </TextField>
      {allowPerson && (
        <TextField select size={size} label="Assign to a person (optional)" value={ownerUid} onChange={(e) => set({ ownerUid: e.target.value })} sx={{ minWidth: 220, flex: 1 }}>
          <MenuItem value="">
            <em>Nobody yet</em>
          </MenuItem>
          {people.map((m) => (
            <MenuItem key={m.uid} value={m.uid} sx={{ fontWeight: m.inDept ? 600 : 400 }}>
              {m.displayName || m.email || m.uid}
              {m.role ? ` · ${ROLE_DEFINITIONS[m.role]?.label || m.role}` : ''}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

/** Small helper for pages: a success/error strip that clears itself. */
export function InlineNotice({ notice, onClose }) {
  if (!notice) return null;
  return (
    <Alert severity={notice.severity || 'success'} sx={{ mb: 2 }} onClose={onClose}>
      {notice.message}
    </Alert>
  );
}

export function ownerNameFor(members, uid) {
  const m = members.find((x) => x.uid === uid);
  return m ? m.displayName || m.email || uid : '';
}

export default NoteDialog;
